import {CancelToken} from 'axios'
import HandlesServerResponses from "./HandlesServerResponses";
import {EXISTING_FORM_INPUT_VALUE} from "../config/vars";
import {isObject} from "../helpers/object_helpers";

export default {

    mixins: [HandlesServerResponses],

    props: {
        // Whether to show an error alert on errors for GET-requests
        // For other requests an error alert is always shown for errors
        withError: {
            type: Boolean,
            default: false
        },

        // Whether to always send the request as a guest even though a user is authenticated to improve caching performance
        asGuest: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {

            // The request currently being sent to the server
            pendingApiRequest: null,
            apiRequestLoading: false,
            apiRequestsCount: 0

        }
    },

    methods: {

        /**
         * Make sure we have a relative path to our API for our proxy.
         *
         * @param action
         * @returns {string}
         */
        getApiEPUrl(action) {
            if (action.startsWith(this.$config.apiUrl)) {
                action = action.substr(this.$config.apiUrl.length);
            } else if (action.indexOf('/') < 0 && action.indexOf('.') >= 0) {
                // In here, we probably have a named api route
                action = this.$api(action)
            }

            return action;
        },

        /**
         * Recursively parses the specified data to be sent to the server.
         *
         * @param {object} data
         * @param {string|null} propNamePrefix
         */
        parseFormData(data, propNamePrefix = null) {
            let parsedData = {};

            if (data && Array.isArray(data) && data.length) {
                data.forEach((itValue, itKey) => {
                    const propName = propNamePrefix ? `${propNamePrefix}[${itKey}]` : itKey;
                    parsedData = {...parsedData, ...this.parseFormValueAsFlatObject(propName, itValue)}
                });
            } else if (data && isObject(data) && Object.keys(data).length) {
                Object.keys(data).forEach(itKey => {
                    const propName = propNamePrefix ? `${propNamePrefix}[${itKey}]` : itKey;
                    const itValue = data[itKey];
                    parsedData = {...parsedData, ...this.parseFormValueAsFlatObject(propName, itValue)}
                });
            } else if (propNamePrefix) {
                // We need to set an empty array for empty objects and arrays
                parsedData[propNamePrefix] = [];
            }

            return parsedData;
        },

        parseFormValueAsFlatObject(key, value) {
            let data = {};
            if (value instanceof FileList) {
                value = Array.from(value)
                for (let i = 0; i < value.length; i++) {
                    data[`${key}[${i}]`] = value[i] !== null && value[i] !== undefined ? value[i] : ''
                }
            } else if (Array.isArray(value) || (isObject(value) && !(value instanceof File))) {
                data = this.parseFormData(value, key)
            } else if (value !== EXISTING_FORM_INPUT_VALUE) {
                // We only add the value under the specified key when the value is not set to '_existing' which means that the existing value for the key shall be used on the server.
                // This enables us to simplify the logic on FormInputs, especially on FormFileInput component on which it's difficult to display the current value.
                data[key] = value !== null && value !== undefined ? value : ''
            }

            return data;
        },

        makeApiRequest(action, data = {}, method = 'get') {
            if (!action) {
                return;
            }

            action = this.getApiEPUrl(action);
            method = method.toLowerCase();
            let params = {};
            const headers = {};

            // We force the generic frontend authorization when sending requests as guests are enforced
            if (this.asGuest) {
                data._guest = true
            }

            if (method === 'get') {
                params = data;
                data = null;
            } else {
                // We need form data to support file uploads
                let submitData = new FormData();

                // FormData does not work with PUT, so we fake the method
                // Set this as the first param, so we do not get a 'Unknown method'-Exception when some params get truncated due to a server misconfiguration
                if (method === 'put') {
                    method = 'post';
                    submitData.append('_method', 'PUT');
                }

                // This is necessary to support file uploads via ajax
                Object.keys(data).forEach(key => {
                    submitData.append(key, data[key]);
                });

                data = submitData;
                headers['Content-Type'] = 'multipart/form-data';
            }

            // Stop the previous request if it is still pending
            if (this.pendingApiRequest) {
                this.pendingApiRequest.cancel();
            }

            this.apiRequestLoading = true;
            this.pendingApiRequest = CancelToken.source();

            return new Promise((resolve, reject) => {
                this.$axios({
                    method: method,
                    url: action,
                    params: params,
                    data: data,
                    cancelToken: this.pendingApiRequest.token,
                    headers: headers
                }).then(response => {
                    this.apiRequestsCount++;
                    this.handleSuccess(response.data);
                    resolve(response);
                }).catch(error => {

                    // Check if error occurred due to missing internet connection of the client
                    if (this.$nuxt.isOffline) {
                        reject(this.$trans('error.offline.message', 'Es scheint als wärst du offline. Bitte überprüfe Deine Netzwerkverbindung, um SPOFERAN vollständig nutzen zu können.'), error);
                        return;
                    }

                    // Check if request was canceled
                    if (!error) {
                        return;
                    }

                    // We show the error only for non-get requests
                    if ((method.toLowerCase() !== 'get' || this.withError)) {
                        this.handleError(error);
                    }

                    reject({
                        message: this.parseErrorMessage(error),
                        response: error
                    });
                }).finally(() => {
                    this.pendingApiRequest = null;
                    this.apiRequestLoading = false;
                })
            })
        }
    }
}

